import type { BoardData, MaterialCategory, MaterialListSettings } from '../model/schema.js';
import { MATERIAL_CATEGORIES } from '../model/schema.js';
import type { MaterialListConfig } from './types.js';

/** Default cable stock lengths in metres (decision U11). */
export const DEFAULT_STOCK_LENGTHS: readonly number[] = [1, 3, 5, 10, 15, 20, 30, 50];

/** Default routing allowance: distance × 1.2 plus 1 m at each end. */
export const DEFAULT_CABLE_ALLOWANCE = { factor: 1.2, perEnd: 1 } as const;

/** Default spare percentage of the categories that carry spares. */
export const DEFAULT_SPARE_PERCENT = 10;

/**
 * Categories that get the board-wide spare percentage unless overridden per category. Spares of
 * consoles, PA or risers make no sense by default; they can still be set per category.
 */
export const SPARE_CATEGORIES: ReadonlySet<MaterialCategory> = new Set([
  'mics',
  'dis',
  'cables_audio',
  'cables_power',
  'cables_network',
  'adapters',
]);

/** Full configuration from the (optional, partial) board settings: missing fields use defaults. */
export function resolveMaterialConfig(settings?: MaterialListSettings): MaterialListConfig {
  const s = settings ?? {};
  const categories = Object.fromEntries(
    MATERIAL_CATEGORIES.map((c) => [c, s.categories?.[c] ?? true]),
  ) as Record<MaterialCategory, boolean>;
  const stock = [...new Set(s.stockLengths ?? [])].sort((a, b) => a - b);
  return {
    categories,
    sparePercent: s.sparePercent ?? DEFAULT_SPARE_PERCENT,
    spareByCategory: { ...s.spareByCategory },
    stockLengths: stock.length > 0 ? stock : [...DEFAULT_STOCK_LENGTHS],
    cableAllowance: { ...(s.cableAllowance ?? DEFAULT_CABLE_ALLOWANCE) },
    groupBy: s.groupBy ?? 'category',
  };
}

/** Configuration of a board (`meta.materialList` resolved against the defaults). */
export function boardMaterialConfig(data: BoardData): MaterialListConfig {
  return resolveMaterialConfig(data.meta.materialList);
}

/** Spare percentage of a category: its override, else the default for spare categories, else 0. */
export function sparePercentFor(config: MaterialListConfig, category: MaterialCategory): number {
  const override = config.spareByCategory[category];
  if (override !== undefined) return override;
  return SPARE_CATEGORIES.has(category) ? config.sparePercent : 0;
}

/** Spares for `count` pieces at `percent` %: rounded up, 0 for nothing or 0 %. */
export function sparesFor(count: number, percent: number): number {
  return count <= 0 || percent <= 0 ? 0 : Math.ceil((count * percent) / 100 - 1e-9);
}

/**
 * Smallest stock length that is at least `length`; beyond the longest stock length the length is
 * rounded up to whole metres. Never returns less than `length`.
 */
export function roundToStock(length: number, stockLengths: readonly number[]): number {
  const hit = [...stockLengths].sort((a, b) => a - b).find((s) => s >= length);
  return hit ?? Math.ceil(length);
}
